import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { http, money } from "../../lib/api";
import { apiError } from "../../context/AuthContext";
import { UploadButton, ImageList } from "./ImageUpload";
import { useAdminList, AdminHead, Field, TextInput, TextArea, Toggle, PrimaryBtn, csv, fromCsv, inputCls } from "./adminUi";

const BLANK = { name: "", product_type: "t-shirt", gender: "unisex", price: 28, destinations: [], tags: [], colors: [{ name: "White", hex: "#FFFFFF" }],
  sizes: ["S", "M", "L", "XL", "2XL"], images: [], description: "", material: "", care: "", featured: false, status: "draft", is_placeholder: false };
const TAGS = ["funny", "couples", "family", "girls-trip", "guys-trip", "cruise"];

const ProductForm = ({ initial, destinations, onDone }) => {
  const [p, setP] = useState({ ...BLANK, ...initial });
  const [colors, setColors] = useState((initial?.colors || BLANK.colors).map((c) => `${c.name}:${c.hex}`).join(", "));
  const set = (k) => (v) => setP({ ...p, [k]: v });
  const toggleIn = (k, v) => set(k)(p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v]);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...p, price: Number(p.price), colors: fromCsv(colors).map((c) => { const [name, hex] = c.split(":"); return { name: name.trim(), hex: (hex || "#FFFFFF").trim() }; }) };
    try {
      if (initial?.slug) await http.put(`/admin/products/${initial.slug}`, body);
      else await http.post("/admin/products", body);
      toast.success("Product saved");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="product-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name"><TextInput required value={p.name} onChange={(e) => set("name")(e.target.value)} data-testid="product-form-name" /></Field>
        <Field label="Price (USD)"><TextInput type="number" step="0.01" min="1" required value={p.price} onChange={(e) => set("price")(e.target.value)} data-testid="product-form-price" /></Field>
        <Field label="Type"><select className={inputCls} value={p.product_type} onChange={(e) => set("product_type")(e.target.value)} data-testid="product-form-type">{["t-shirt", "hat", "hoodie", "tank-top"].map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="Fit"><select className={inputCls} value={p.gender} onChange={(e) => set("gender")(e.target.value)} data-testid="product-form-gender">{["unisex", "men", "women"].map((t) => <option key={t}>{t}</option>)}</select></Field>
      </div>
      {initial?.printify_product_id ? (
        <p className="rounded-xl bg-turq/10 p-3 text-sm text-navy" data-testid="printify-variant-note">Colors ({p.colors.map((c) => c.name).join(", ")}) and sizes ({p.sizes.join(", ")}) come from Printify's enabled variants — change them in Printify, then Sync. Production cost: ${Math.min(...p.variants.map((v) => v.cost)).toFixed(2)}–${Math.max(...p.variants.map((v) => v.cost)).toFixed(2)}; your price must be higher.</p>
      ) : (
        <>
          <Field label="Colors" hint="Name:#hex, comma separated"><TextInput value={colors} onChange={(e) => setColors(e.target.value)} data-testid="product-form-colors" /></Field>
          <Field label="Sizes" hint="Comma separated"><TextInput value={csv(p.sizes)} onChange={(e) => set("sizes")(fromCsv(e.target.value))} data-testid="product-form-sizes" /></Field>
        </>
      )}
      <Field label="Product images" hint="First image is the main mockup. Upload files or paste URLs (one per line).">
        <div className="space-y-3">
          <ImageList images={p.images} onChange={set("images")} />
          <UploadButton kind="product" multiple label="Upload mockups" onUploaded={(url) => setP((cur) => ({ ...cur, images: [...cur.images, url] }))} testid="product-image-upload" />
          <TextArea rows={2} value={p.images.join("\n")} onChange={(e) => set("images")(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid="product-form-images" />
        </div>
      </Field>
      <Field label="Destinations">
        <div className="flex flex-wrap gap-2">{destinations.map((d) => (
          <button type="button" key={d.slug} onClick={() => toggleIn("destinations", d.slug)} className={`px-3 h-8 rounded-full text-xs font-semibold border ${p.destinations.includes(d.slug) ? "bg-navy text-white border-navy" : "border-border"}`} data-testid={`product-form-dest-${d.slug}`}>{d.name}</button>
        ))}</div>
      </Field>
      <Field label="Collections">
        <div className="flex flex-wrap gap-2">{TAGS.map((t) => (
          <button type="button" key={t} onClick={() => toggleIn("tags", t)} className={`px-3 h-8 rounded-full text-xs font-semibold border ${p.tags.includes(t) ? "bg-tpink text-white border-tpink" : "border-border"}`} data-testid={`product-form-tag-${t}`}>{t}</button>
        ))}</div>
      </Field>
      <Field label="Description"><TextArea rows={3} value={p.description} onChange={(e) => set("description")(e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Material"><TextArea rows={2} value={p.material} onChange={(e) => set("material")(e.target.value)} /></Field>
        <Field label="Care"><TextArea rows={2} value={p.care} onChange={(e) => set("care")(e.target.value)} /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        <Field label="Status"><select className={inputCls} value={p.status} onChange={(e) => set("status")(e.target.value)} data-testid="product-form-status">{["draft", "active", "archived"].map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Toggle label="Featured" checked={p.featured} onChange={set("featured")} testid="product-form-featured" />
        <Toggle label="Placeholder (can't be published)" checked={p.is_placeholder} onChange={set("is_placeholder")} testid="product-form-placeholder" />
      </div>
      <PrimaryBtn className="w-full" data-testid="product-form-save">Save product</PrimaryBtn>
    </form>
  );
};

export default function AdminProducts() {
  const { data, reload } = useAdminList("/admin/products");
  const { data: dests } = useAdminList("/admin/destinations");
  const [edit, setEdit] = useState(null);
  const archive = async (slug) => {
    await http.delete(`/admin/products/${slug}`);
    toast.success("Product archived");
    reload();
  };
  return (
    <div data-testid="admin-products">
      <AdminHead title="Products" sub="Only 'active' products are purchasable. Use Printify → Sync products to import; imported items arrive as drafts.">
        <PrimaryBtn onClick={() => setEdit({})} className="flex items-center gap-2" data-testid="add-product-button"><Plus className="w-4 h-4" /> Add product</PrimaryBtn>
      </AdminHead>
      <div className="rounded-3xl bg-white shadow-sm overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="text-left text-navy/50 text-xs uppercase tracking-wider"><tr><th className="p-4">Product</th><th>Price</th><th>Status</th><th>Destinations</th><th>Cost</th><th>Sold</th><th /></tr></thead>
          <tbody>
            {data?.map((p) => (
              <tr key={p.slug} className="border-t border-border" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-4 flex items-center gap-3"><img src={p.images?.[0]} alt="" className="w-12 h-12 rounded-xl object-cover bg-sand" /><span className="font-semibold text-navy">{p.name}{p.is_placeholder && <span className="ml-2 text-[10px] bg-sunset text-white rounded-full px-2 py-0.5">PLACEHOLDER</span>}{p.printify_product_id && <span className="ml-2 text-[10px] bg-turq text-white rounded-full px-2 py-0.5">PRINTIFY</span>}</span></td>
                <td>{money(p.price)}</td>
                <td><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${p.status === "active" ? "bg-emerald-100 text-emerald-800" : p.status === "draft" ? "bg-sun/40 text-navy" : "bg-navy/10 text-navy/60"}`}>{p.status}</span></td>
                <td className="text-navy/60 text-xs">{csv(p.destinations)}</td>
                <td className="text-xs">{p.printify_product_id ? <span title="Printify production cost (lowest variant)">from {money(p.min_cost)}{p.price <= Math.max(...(p.variants || []).map((v) => v.cost)) && <b className="block text-red-600">below cost!</b>}</span> : "—"}</td>
                <td>{p.sales_count}</td>
                <td className="pr-4 text-right whitespace-nowrap">
                  <button onClick={() => setEdit(p)} className="font-semibold text-[#0078a0] mr-4" data-testid={`edit-product-${p.slug}`}>Edit</button>
                  {p.status !== "archived" && <button onClick={() => archive(p.slug)} className="font-semibold text-tpink" data-testid={`archive-product-${p.slug}`}>Archive</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogTitle className="text-navy text-xl">{edit?.slug ? "Edit product" : "New product"}</DialogTitle>
          {edit && <ProductForm initial={edit} destinations={dests || []} onDone={() => { setEdit(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
